/**
 * Component: Newsletter Form
 * ------------------------------------------------------------------------------
 * Newsletter sign-up form custom component.
 *
 * - This is intended to be imported into a given section, followed by
 *   customElements.define('...')
 *
 * @namespace NewsletterForm
 */
import { getCurrentStore } from '~/helpers/utils'
import { validateEmail } from '~/helpers/strings'

export default class NewsletterForm extends window.HTMLElement {
  static observedAttributes = ['state']

  /**
   * Initialise.
   */
  constructor() {
    super()

    this.setProps()
    this.setSelectors()
    this.setDefaultState()

    this.handleSubmit = this.handleSubmit.bind(this)
    this.handleInputChange = this.handleInputChange.bind(this)
  }

  /**
   * Get an object for the params that are sent to Datitude.
   * @returns {Boolean|Object}
   */
  getDatitudeParams() {
    if (!this.props.datitudeFormName || !this.props.datitudeFormType) {
      mint.Log('warning', 'NewsletterForm: Unable to get Datitude params')

      return false
    }

    const isStaging = getCurrentStore()
    const datitudeAccount = isStaging ? 'mvqa1' : 'mv01'

    return {
      account: datitudeAccount,
      form_name: this.props.datitudeFormName,
      form_type: this.props.datitudeFormType,
      email: this.emailAddress,
      email_subscriber: true,
    }
  }

  /**
   * Set selectors
   */
  setSelectors() {
    this.selectors = {
      emailInput: this.querySelector('[js-input="email"]'),
      button: this.querySelector('[js-button="submit"]'),
      form: this.querySelector('[js-form="newsletter"]'),
    }
  }

  /**
   * Set the properties from JSON data.
   *
   * @throws {Error} Throws an error if there is an issue parsing the JSON.
   */
  setProps() {
    try {
      this.props = JSON.parse(
        this.querySelector('[js-newsletter-props]').innerHTML,
      )
    } catch (error) {
      mint.Log('error', 'NewsletterForm: Unable to parse the JSON', error)

      this.props = {}
    }
  }

  /**
   * Set the default state.
   */
  setDefaultState() {
    this.emailAddress = mint?.customer?.email || ''
  }

  /**
   * The element is now defined on the page, so run functionality.
   */
  connectedCallback() {
    if (this.selectors.emailInput) {
      this.selectors.emailInput.value = this.emailAddress
    }

    this.setEventListeners()
  }

  /**
   * Invokes each time the custom element is disconnected from the document's
   * DOM.
   */
  disconnectedCallback() {
    this.removeEventListeners()
  }

  /**
   * Callback for attribute changes.
   * @param {string} name - The name of the changed attribute.
   * @param {string} oldValue - The previous value of the attribute.
   * @param {string} newValue - The new value of the attribute.
   */
  attributeChangedCallback(name, oldValue, newValue) {

    /**
     *  Assume that state can be one of the following: `error`, `emailError`,
     * `submitting`, `ready`, `success`
     */
    if (name === 'state' && newValue !== 'ready') {
      this.selectors.button?.setAttribute('disabled', 'disabled')
    } else {
      this.selectors.button?.removeAttribute('disabled')
    }
  }

  /**
   * Handle form submission events.
   * @param {Event} event - Form `submit` event.
   * @async
   */
  async handleSubmit(event) {
    event.preventDefault()

    this.handleInputChange() // If the email was preselected/autofilled
    const isEmailError = !validateEmail(this.emailAddress)

    if (isEmailError) {
      this.setAttribute('state', 'emailError')
      return
    }

    this.datitudeParams = this.getDatitudeParams()

    if (!this.datitudeParams) {
      mint.Log('warning', 'NewsletterForm: Unable to get Datitude params')
      return
    }

    this.setAttribute('state', 'submitting')

    try {
      await this.sendDatitudeEvent()
      this.sendBloomreachEvent()
      this.sendGaEvent()
      this.setAttribute('state', 'success')

      if (this.selectors.emailInput) {
        this.selectors.emailInput.value = ''
      }

      CF.customerReady(() => {
        this.updateCustomerFields()
      })
    } catch (error) {
      this.setAttribute('state', 'error')
      console.error('Error in email sign up form:', error)
    }
  }

  /**
   * Listen for an input changing.
   * @param {Event} event - Input's `change` or `input` event.
   */
  handleInputChange() {
    this.setAttribute('state', 'ready')
    this.emailAddress = this.selectors.emailInput?.value
  }

  /**
   * Send the subscription event to Datitude.
   * @async
   */
  async sendDatitudeEvent() {
    const response = await fetch(
      'https://app.datitude.co.uk/public-api/subscriber-centre/subscribers',
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          API_VERSION: '1.1',
        },
        body: JSON.stringify(this.datitudeParams),
      },
    )

    if (!response.ok) {
      throw new Error('Datitude API error', response)
    }
  }

  /**
   * Dispatch the Bloomreach event.
   */
  sendBloomreachEvent() {
    mint.EventBus.dispatch(
      'bloomreach:identify',
      JSON.stringify({ email: this.emailAddress }),
    )
  }

  /**
   * Trigger a Google Analytics event if the setting is enabled and the `gtag`
   * function exists.
   */
  sendGaEvent() {
    if (
      !this.props.gaEnabled ||
      !this.props.gaEventName ||
      typeof gtag !== 'function'
    ) {
      return
    }

    gtag('event', this.props.gaEventName, {
      page_location: location.href,
      page_path: location.pathname,
      page_title: document.title,
    })
  }

  /**
   * If the user has a customer account, update their marketing preferences
   * via the Customer Fields API.
   * - If the user is not logged in, set the submitted email address to
   *   create a guest account.
   * - If the user is logged in but the submitted email address does not match
   *   their account email address, avoid updating their preferences.
   */
  updateCustomerFields() {
    if (!CF?.customer?.id) {
      CF.customer.set('email', this.emailAddress)
    }

    if (
      CF?.customer?.data?.email &&
      CF?.customer?.data?.email !== this.emailAddress
    ) {
      return
    }

    CF.customer.set('accepts_marketing', true)

    CF.customer.set('mp_email', {
      strdate: new Date().toISOString(),
      src: location.href,
      value: true,
    })

    CF.customer.save().catch((error) => {
      console.error('Customer save error:', error)
    })
  }

  /**
   * Set Event Listeners.
   */
  setEventListeners() {
    this.selectors.form?.addEventListener('submit', this.handleSubmit)
    this.selectors.button?.addEventListener('click', this.handleSubmit)
    this.selectors.emailInput?.addEventListener(
      'change',
      this.handleInputChange,
    )
  }

  /**
   * Remove Event Listeners.
   */
  removeEventListeners() {
    this.selectors.form?.removeEventListener('submit', this.handleSubmit)
    this.selectors.button?.addEventListener('click', this.handleSubmit)
    this.selectors.emailInput?.removeEventListener(
      'change',
      this.handleInputChange,
    )
  }
}
